import axios from 'axios';
import { getAuthHeaders } from '../utils/authUtils';

const base = () => `${process.env.REACT_APP_API_URL}/api/v3/task-tracking`;
const tasks = () => `${process.env.REACT_APP_API_URL}/api/v3/tasks`;
const config = () => ({ headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } });

/**
 * Task mutations and reads. Every mutation returns the fresh task detail
 * ({ task, stages, permissions, earlierTasks, contentFolderPath, taskFolderPath }).
 * Errors keep the server's { code, message } body on error.response.data.
 */
const TaskTrackingService = {
    summary: async () => (await axios.get(`${base()}/me/summary`, config())).data,
    get: async (taskId) => (await axios.get(`${base()}/${taskId}`, config())).data,
    events: async (taskId) => (await axios.get(`${base()}/${taskId}/events`, config())).data,
    assignableUsers: async (contentType, contentId) =>
        (await axios.get(`${base()}/assignable-users`, { ...config(), params: { contentType, contentId } })).data,
    forContents: async (refs) => (await axios.post(`${base()}/for-contents`, refs, config())).data,
    // The Google Drive or YouTube link a video was added from, or null.
    videoSourceLink: async (videoId) =>
        (await axios.get(`${base()}/source-link`, { ...config(), params: { videoId } })).data.sourceUrl,

    start: async (body) => (await axios.post(`${base()}/start`, body, config())).data,
    startBulk: async (body) => (await axios.post(`${base()}/start-bulk`, body, config())).data,

    // New task before the file exists. plan returns { planId, taskIds }; linkPlan { contentId, title, linked, total, failed }.
    plan: async (body) => (await axios.post(`${base()}/plan`, body, config())).data,
    linkPlan: async (planId, body) => (await axios.post(`${base()}/plan/${planId}/link`, body, config())).data,
    // Start video. startVideo and startVideoAgain return { videoId, name, linked, total, failed, notOnVideo }.
    videoFiles: async (contentId) =>
        (await axios.get(`${base()}/video-files`, { ...config(), params: { contentId } })).data,
    startVideo: async (planId, body) => (await axios.post(`${base()}/plan/${planId}/start-video`, body, config())).data,
    startVideoAgain: async (videoId, body) =>
        (await axios.post(`${base()}/video/${videoId}/start-again`, body, config())).data,
    linkSearch: async (contentType, q) =>
        (await axios.get(`${base()}/link-search`, { ...config(), params: { contentType, q } })).data,

    startStage: async (taskId, body = {}) => (await axios.post(`${base()}/${taskId}/start-stage`, body, config())).data,
    complete: async (taskId, body = {}) => (await axios.post(`${base()}/${taskId}/complete`, body, config())).data,
    block: async (taskId, body) => (await axios.post(`${base()}/${taskId}/block`, body, config())).data,
    unblock: async (taskId, body = {}) => (await axios.post(`${base()}/${taskId}/unblock`, body, config())).data,
    comment: async (taskId, body) => (await axios.post(`${base()}/${taskId}/comment`, body, config())).data,
    flag: async (taskId, body) => (await axios.post(`${base()}/${taskId}/flag`, body, config())).data,
    override: async (taskId, body) => (await axios.post(`${base()}/${taskId}/override`, body, config())).data,
    hold: async (taskId, body) => (await axios.post(`${base()}/${taskId}/hold`, body, config())).data,
    resume: async (taskId, body = {}) => (await axios.post(`${base()}/${taskId}/resume`, body, config())).data,
    cancel: async (taskId, body) => (await axios.post(`${base()}/${taskId}/cancel`, body, config())).data,
    remove: async (taskId, version) =>
        (await axios.delete(`${base()}/${taskId}`, { ...config(), params: { version } })).data,
    removeBulk: async (taskIds) => (await axios.post(`${base()}/delete-bulk`, { taskIds }, config())).data,
    /** body: { taskIds, stageKey, assignee, grantContentAccess }. Returns { changed, leftAlone, failed }. */
    assignBulk: async (body) => (await axios.post(`${base()}/assign-bulk`, body, config())).data,
    update: async (taskId, body) => (await axios.post(`${base()}/${taskId}/update`, body, config())).data,
    resolveAttention: async (taskId, body) =>
        (await axios.post(`${base()}/${taskId}/resolve-attention`, body, config())).data,

    /**
     * Completes a stage with fetch keepalive so it still reaches the server while the page
     * unloads (sendBeacon can't carry the auth headers).
     */
    completeKeepalive: (taskId, body = {}) =>
        fetch(`${base()}/${taskId}/complete`, {
            method: 'POST',
            keepalive: true,
            headers: config().headers,
            body: JSON.stringify(body),
        }),

    taskTypes: async () =>
        (await axios.get(`${process.env.REACT_APP_API_URL}/api/v3/task-types`, config())).data,
};

/** The server's error code (e.g. ACCESS_CONFIRMATION_REQUIRED, STALE_VERSION), if any. */
export const taskErrorCode = (error) => error?.response?.data?.code;

/** A readable message for a failed task request. */
export const taskErrorMessage = (error) =>
    error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.';

export default TaskTrackingService;
